import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { RefreshCw, Wallet, CheckCircle2, Download, TrendingUp, Trash2, Pencil } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { ipdBillingApi } from '@/api/ipdBilling';
import { useAuth } from '@/contexts/AuthContext';
import { useCanMutateFrontDeskIpd, useFrontDeskBasePath } from '@/hooks/useFrontDeskBasePath';
import {
  formatDateTime,
  istDatetimeLocalToIso,
  toIstDateInputValue,
  toIstDatetimeLocalValue,
} from '@/utils/dateUtils';
import type { IPDPendingCollection, PaymentMethod } from '@/types/ipdBilling';

const IST_TIMEZONE = 'Asia/Kolkata';

const PAYMENT_METHODS: PaymentMethod[] = [
  'CASH',
  'CARD',
  'UPI',
  'BANK_TRANSFER',
  'CHEQUE',
  'OTHER',
];

function formatCurrency(n: number) {
  return `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** Whole calendar days in IST from discharge date through today (inclusive of discharge day = 0). */
function ipdPendingAgeingDays(dischargeDateIso: string | null | undefined): number | null {
  const fromKey = dischargeDateIso ? toIstDateInputValue(dischargeDateIso) : '';
  const toKey = toIstDateInputValue(new Date());
  if (!fromKey || !toKey) return null;
  const from = new Date(`${fromKey}T12:00:00+05:30`).getTime();
  const to = new Date(`${toKey}T12:00:00+05:30`).getTime();
  if (Number.isNaN(from) || Number.isNaN(to)) return null;
  return Math.max(0, Math.round((to - from) / 86_400_000));
}

function formatAgeingLabel(days: number | null): string {
  if (days == null) return '—';
  if (days === 0) return '0 days';
  if (days === 1) return '1 day';
  return `${days} days`;
}

function settlementDiffPercent(billed: number, settled: number): string {
  if (billed <= 0.009) return '—';
  const pct = ((billed - settled) / billed) * 100;
  if (Math.abs(pct) < 0.01) return '0%';
  return `${pct.toFixed(1)}%`;
}

function effectiveSettledAmount(row: IPDPendingCollection): number {
  return row.settledAmount != null && Number.isFinite(row.settledAmount)
    ? row.settledAmount
    : row.amount;
}

function sumSettledAmounts(rows: IPDPendingCollection[]) {
  return Math.round(rows.reduce((sum, row) => sum + effectiveSettledAmount(row), 0) * 100) / 100;
}

function istMonthKey(iso: string): string {
  const dt = new Date(iso);
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: IST_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
  }).formatToParts(dt);
  const year = parts.find((p) => p.type === 'year')?.value ?? '';
  const month = parts.find((p) => p.type === 'month')?.value ?? '';
  return `${year}-${month}`;
}

function istMonthLabel(monthKey: string): string {
  const [year, month] = monthKey.split('-');
  const dt = new Date(`${year}-${month}-01T00:00:00+05:30`);
  return dt.toLocaleDateString('en-IN', {
    month: 'short',
    year: 'numeric',
    timeZone: IST_TIMEZONE,
  });
}

function groupCollectedByMonth(rows: IPDPendingCollection[]) {
  const consolidated = consolidateByAdmission(rows.filter((r) => !!r.collectedAt));
  const map = new Map<string, { amount: number; count: number }>();
  for (const row of consolidated) {
    if (!row.latestAt) continue;
    const key = istMonthKey(row.latestAt);
    const bucket = map.get(key) ?? { amount: 0, count: 0 };
    bucket.amount += row.settledTotal;
    bucket.count += 1;
    map.set(key, bucket);
  }
  return [...map.entries()]
    .sort(([a], [b]) => b.localeCompare(a))
    .map(([monthKey, stats]) => ({
      monthKey,
      label: istMonthLabel(monthKey),
      amount: Math.round(stats.amount * 100) / 100,
      count: stats.count,
    }));
}

type ConsolidatedAdmissionRow = {
  admissionId: string;
  ipdNumber: string | null;
  patientName: string | null;
  patientUhid: string | null;
  patientPhone: string | null;
  billedTotal: number;
  settledTotal: number;
  partnerLabel: string;
  count: number;
  latestAt: string | null;
  dischargeDate: string | null;
  collectedByName: string | null;
  notes: string | null;
  rows: IPDPendingCollection[];
};

function consolidateByAdmission(rows: IPDPendingCollection[]): ConsolidatedAdmissionRow[] {
  const map = new Map<string, ConsolidatedAdmissionRow>();
  for (const row of rows) {
    const key = row.admissionId;
    const patient = row.admission?.queue?.patient;
    const partner = row.financingPartnerName?.trim() || '';
    const at = row.status === 'PENDING' ? row.createdAt : row.collectedAt;
    const existing = map.get(key);
    if (!existing) {
      map.set(key, {
        admissionId: key,
        ipdNumber: row.admission?.queue?.ipdNumber ?? null,
        patientName: patient?.name ?? null,
        patientUhid: patient?.uhid ?? null,
        patientPhone: patient?.phone ?? null,
        billedTotal: Number(row.amount || 0),
        settledTotal: effectiveSettledAmount(row),
        partnerLabel: partner,
        count: 1,
        latestAt: at ?? null,
        dischargeDate: row.admission?.dischargeDate ?? null,
        collectedByName: row.collectedBy?.name ?? null,
        notes: row.notes ?? null,
        rows: [row],
      });
      continue;
    }
    if (!existing.dischargeDate && row.admission?.dischargeDate) {
      existing.dischargeDate = row.admission.dischargeDate;
    }
    existing.billedTotal = Math.round((existing.billedTotal + Number(row.amount || 0)) * 100) / 100;
    existing.settledTotal =
      Math.round((existing.settledTotal + effectiveSettledAmount(row)) * 100) / 100;
    existing.count += 1;
    existing.rows.push(row);
    if (partner && !existing.partnerLabel.includes(partner)) {
      existing.partnerLabel = existing.partnerLabel
        ? `${existing.partnerLabel}, ${partner}`
        : partner;
    }
    if (at && (!existing.latestAt || new Date(at) > new Date(existing.latestAt))) {
      existing.latestAt = at;
    }
    if (row.collectedBy?.name) existing.collectedByName = row.collectedBy.name;
    if (row.notes) {
      existing.notes = existing.notes ? `${existing.notes} | ${row.notes}` : row.notes;
    }
  }
  return [...map.values()].sort((a, b) => {
    const ta = a.latestAt ? new Date(a.latestAt).getTime() : 0;
    const tb = b.latestAt ? new Date(b.latestAt).getTime() : 0;
    return tb - ta;
  });
}

function csvEscape(value: string | number | null | undefined): string {
  const raw = value == null ? '' : String(value);
  if (/[",\n]/.test(raw)) return `"${raw.replace(/"/g, '""')}"`;
  return raw;
}

function rowToCsv(row: IPDPendingCollection, status: 'PENDING' | 'COLLECTED'): string[] {
  const patient = row.admission?.queue?.patient;
  const base = [
    patient?.name ?? '',
    patient?.uhid ?? '',
    patient?.phone ?? '',
    row.admission?.queue?.ipdNumber ?? '',
    row.amount.toFixed(2),
  ];
  if (status === 'COLLECTED') {
    const settled = effectiveSettledAmount(row);
    base.push(settled.toFixed(2));
    base.push(settlementDiffPercent(row.amount, settled));
  }
  base.push(
    row.financingPartnerName ?? '',
    row.label,
    row.notes ?? '',
    status === 'PENDING'
      ? row.createdAt
        ? formatDateTime(row.createdAt)
        : ''
      : row.collectedAt
        ? formatDateTime(row.collectedAt)
        : '',
    status === 'COLLECTED' ? (row.collectedBy?.name ?? '') : '',
  );
  return base;
}

function buildReportCsv(
  pendingRows: IPDPendingCollection[],
  collectedRows: IPDPendingCollection[],
  pendingTotal: number,
  monthlyCollected: ReturnType<typeof groupCollectedByMonth>
): string {
  const lines: string[] = [];
  const push = (...cells: (string | number)[]) => lines.push(cells.map(csvEscape).join(','));
  const pendingAdmissions = consolidateByAdmission(pendingRows).length;
  const collectedAdmissions = consolidateByAdmission(collectedRows).length;

  push('IPD Pending Collection Report');
  push(`Generated (IST)`, formatDateTime(new Date()));
  push('');
  push('Summary');
  push('Metric', 'Value');
  push('Total collectable (pending) as on date', pendingTotal.toFixed(2));
  push('Pending IPD admissions (consolidated)', pendingAdmissions);
  push('Pending marker rows', pendingRows.length);
  push('Total collected (all time)', sumSettledAmounts(collectedRows).toFixed(2));
  push('Collected IPD admissions (consolidated)', collectedAdmissions);
  push('Collected marker rows', collectedRows.length);
  push('');
  push('Collected by month (IST)');
  push('Month', 'Settled amount', 'IPD admissions');
  if (monthlyCollected.length === 0) {
    push('—', '0.00', '0');
  } else {
    for (const month of monthlyCollected) {
      push(month.label, month.amount.toFixed(2), month.count);
    }
  }
  push('');
  push('Pending — detail (marker rows)');
  push(
    'Patient',
    'UHID',
    'Phone',
    'IPD #',
    'Amount',
    'Financing partner',
    'Status',
    'Notes',
    'Recorded (IST)',
    'Collected by'
  );
  if (pendingRows.length === 0) {
    push('—', '', '', '', '0.00', '', 'PENDING', '', '', '');
  } else {
    for (const row of pendingRows) {
      push(...rowToCsv(row, 'PENDING'));
    }
  }
  push('');
  push('Collected — detail (marker rows)');
  push(
    'Patient',
    'UHID',
    'Phone',
    'IPD #',
    'Billed amount',
    'Settled amount',
    '% difference',
    'Financing partner',
    'Status',
    'Notes',
    'Collected (IST)',
    'Collected by'
  );
  if (collectedRows.length === 0) {
    push('—', '', '', '', '0.00', '0.00', '0%', '', 'COLLECTED', '', '', '');
  } else {
    for (const row of collectedRows) {
      push(...rowToCsv(row, 'COLLECTED'));
    }
  }

  return `\uFEFF${lines.join('\n')}`;
}

function downloadTextFile(filename: string, content: string) {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

export default function IPDPendingCollectionPage() {
  const { user } = useAuth();
  const canMutateIpd = useCanMutateFrontDeskIpd();
  const canDeletePendingThirdParty =
    user?.role === 'HOSPITAL_ADMIN' || user?.role === 'SUPER_ADMIN';
  const basePath = useFrontDeskBasePath();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<'PENDING' | 'COLLECTED'>('PENDING');
  const [collectTarget, setCollectTarget] = useState<ConsolidatedAdmissionRow | null>(null);
  const [collectForm, setCollectForm] = useState({
    paymentMode: 'BANK_TRANSFER' as PaymentMethod,
    settledAmount: '',
    receiptNumber: '',
    paymentDate: toIstDatetimeLocalValue(new Date()),
    notes: '',
  });
  const [reviseTarget, setReviseTarget] = useState<ConsolidatedAdmissionRow | null>(null);
  const [reviseForm, setReviseForm] = useState({ settledAmount: '', notes: '' });
  const [downloading, setDownloading] = useState(false);

  const {
    data: pendingData,
    isLoading: pendingLoading,
    refetch: refetchPending,
    isFetching: pendingFetching,
  } = useQuery({
    queryKey: ['ipd-pending-collections', 'PENDING'],
    queryFn: () => ipdBillingApi.listPendingCollections(undefined, 'PENDING'),
  });

  const {
    data: collectedData,
    isLoading: collectedLoading,
    refetch: refetchCollected,
    isFetching: collectedFetching,
  } = useQuery({
    queryKey: ['ipd-pending-collections', 'COLLECTED'],
    queryFn: () => ipdBillingApi.listPendingCollections(undefined, 'COLLECTED'),
  });

  const pendingRows = pendingData?.data?.data ?? [];
  const collectedRows = collectedData?.data?.data ?? [];
  const pendingConsolidated = useMemo(() => consolidateByAdmission(pendingRows), [pendingRows]);
  const collectedConsolidated = useMemo(
    () => consolidateByAdmission(collectedRows),
    [collectedRows]
  );
  const consolidatedRows = tab === 'PENDING' ? pendingConsolidated : collectedConsolidated;
  const pendingTotal = useMemo(
    () => Math.round(pendingRows.reduce((sum, row) => sum + row.amount, 0) * 100) / 100,
    [pendingRows]
  );
  const monthlyCollected = useMemo(
    () => groupCollectedByMonth(collectedRows),
    [collectedRows]
  );
  const tabBilledTotal = useMemo(
    () =>
      Math.round(consolidatedRows.reduce((sum, row) => sum + row.billedTotal, 0) * 100) / 100,
    [consolidatedRows]
  );
  const tabSettledTotal = useMemo(
    () =>
      Math.round(consolidatedRows.reduce((sum, row) => sum + row.settledTotal, 0) * 100) / 100,
    [consolidatedRows]
  );

  const isLoading = tab === 'PENDING' ? pendingLoading : collectedLoading;
  const isFetching = pendingFetching || collectedFetching;

  const refetchAll = () => {
    void refetchPending();
    void refetchCollected();
  };

  const downloadReport = () => {
    try {
      setDownloading(true);
      const csv = buildReportCsv(pendingRows, collectedRows, pendingTotal, monthlyCollected);
      const dateStamp = toIstDatetimeLocalValue(new Date()).slice(0, 10);
      downloadTextFile(`ipd-pending-collection-${dateStamp}.csv`, csv);
      toast.success('Report downloaded');
    } catch {
      toast.error('Failed to download report');
    } finally {
      setDownloading(false);
    }
  };

  const collectMut = useMutation({
    mutationFn: () => {
      const settled = parseFloat(collectForm.settledAmount);
      return ipdBillingApi.collectAllPendingForAdmission(collectTarget!.admissionId, {
        paymentMode: collectForm.paymentMode,
        settledAmount: settled,
        receiptNumber: collectForm.receiptNumber.trim() || undefined,
        paymentDate: collectForm.paymentDate
          ? istDatetimeLocalToIso(collectForm.paymentDate)
          : undefined,
        notes: collectForm.notes.trim() || undefined,
      });
    },
    onSuccess: () => {
      const admissionId = collectTarget?.admissionId;
      toast.success('Consolidated settlement recorded on IPD billing');
      setCollectTarget(null);
      setCollectForm({
        paymentMode: 'BANK_TRANSFER',
        settledAmount: '',
        receiptNumber: '',
        paymentDate: toIstDatetimeLocalValue(new Date()),
        notes: '',
      });
      void queryClient.invalidateQueries({ queryKey: ['ipd-pending-collections'] });
      if (admissionId) {
        void queryClient.invalidateQueries({ queryKey: ['ipd-balance', admissionId] });
        void queryClient.invalidateQueries({ queryKey: ['ipd-deposits', admissionId] });
        void queryClient.invalidateQueries({ queryKey: ['ipd-settle', admissionId] });
      }
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.message || 'Failed to record collection'),
  });

  const reviseSettledMut = useMutation({
    mutationFn: () => {
      const settled = parseFloat(reviseForm.settledAmount);
      return ipdBillingApi.reviseCollectedSettledAmount(reviseTarget!.admissionId, {
        settledAmount: settled,
        notes: reviseForm.notes.trim() || undefined,
      });
    },
    onSuccess: () => {
      const admissionId = reviseTarget?.admissionId;
      toast.success('Settled amount revised — revenue reports will use the new figure');
      setReviseTarget(null);
      setReviseForm({ settledAmount: '', notes: '' });
      void queryClient.invalidateQueries({ queryKey: ['ipd-pending-collections'] });
      if (admissionId) {
        void queryClient.invalidateQueries({ queryKey: ['ipd-balance', admissionId] });
        void queryClient.invalidateQueries({ queryKey: ['ipd-deposits', admissionId] });
        void queryClient.invalidateQueries({ queryKey: ['ipd-settle', admissionId] });
      }
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.message || 'Failed to revise settled amount'),
  });

  const deletePendingMut = useMutation({
    mutationFn: async (rows: IPDPendingCollection[]) => {
      for (const row of rows) {
        await ipdBillingApi.deletePendingCollection(row.id);
      }
      return rows;
    },
    onSuccess: (rows) => {
      const admissionId = rows[0]?.admissionId;
      toast.success(
        rows.length > 1
          ? `Deleted ${rows.length} pending third-party amounts`
          : 'Pending third-party amount deleted'
      );
      void queryClient.invalidateQueries({ queryKey: ['ipd-pending-collections'] });
      if (admissionId) {
        void queryClient.invalidateQueries({ queryKey: ['ipd-balance', admissionId] });
        void queryClient.invalidateQueries({ queryKey: ['ipd-deposits', admissionId] });
        void queryClient.invalidateQueries({ queryKey: ['ipd-settle', admissionId] });
        void queryClient.invalidateQueries({
          queryKey: ['ipd-admission-pending-collections', admissionId],
        });
      }
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.message || 'Failed to delete pending third-party amount'),
  });

  const isPendingTab = tab === 'PENDING';

  return (
    <div className="space-y-6 p-4 md:p-6">
      {!canMutateIpd && user?.role === 'OPERATIONS_MANAGER' ? (
        <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
          View only — you can review pending and collected amounts; marking collected or deleting entries requires reception or admin.
        </div>
      ) : null}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">IPD Pending Collection</h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Amounts flagged as <strong>pending from third party</strong> on Advance, Top Up, or Full
            &amp; Final deposits (e.g. financing partner / TPA). Each IPD shows one{' '}
            <strong>consolidated</strong> collectable amount. Use <strong>Mark collected</strong> to
            record a single final settlement against all open pendings for that IPD — that settled
            amount is revenue.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={downloadReport}
            disabled={downloading || pendingLoading || collectedLoading}
          >
            <Download className={`h-4 w-4 mr-2 ${downloading ? 'animate-pulse' : ''}`} />
            {downloading ? 'Downloading…' : 'Download report'}
          </Button>
          <Button variant="outline" size="sm" onClick={refetchAll} disabled={isFetching}>
            <RefreshCw className={`h-4 w-4 mr-2 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-medium flex items-center gap-2">
              <Wallet className="h-4 w-4 text-amber-700" />
              Collectable as on date
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-semibold text-amber-800">{formatCurrency(pendingTotal)}</p>
            <p className="text-sm text-muted-foreground mt-1">
              {pendingConsolidated.length} IPD{' '}
              {pendingConsolidated.length === 1 ? 'admission' : 'admissions'} (consolidated)
              {pendingRows.length > pendingConsolidated.length
                ? ` · ${pendingRows.length} marker rows`
                : ''}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-medium flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-emerald-600" />
              Collected by month
            </CardTitle>
          </CardHeader>
          <CardContent>
            {collectedLoading ? (
              <p className="text-sm text-muted-foreground">Loading…</p>
            ) : monthlyCollected.length === 0 ? (
              <p className="text-sm text-muted-foreground">No collections recorded yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Month (IST)</TableHead>
                      <TableHead className="text-right">Settled amount</TableHead>
                      <TableHead className="text-right">IPDs</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {monthlyCollected.map((month) => (
                      <TableRow key={month.monthKey}>
                        <TableCell>{month.label}</TableCell>
                        <TableCell className="text-right font-medium text-emerald-700">
                          {formatCurrency(month.amount)}
                        </TableCell>
                        <TableCell className="text-right text-muted-foreground">
                          {month.count}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                  <TableFooter>
                    <TableRow>
                      <TableCell className="font-semibold">Total</TableCell>
                      <TableCell className="text-right font-semibold text-emerald-700">
                        {formatCurrency(sumSettledAmounts(collectedRows))}
                      </TableCell>
                      <TableCell className="text-right font-semibold text-muted-foreground">
                        {collectedConsolidated.length}
                      </TableCell>
                    </TableRow>
                  </TableFooter>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Tabs value={tab} onValueChange={(v) => setTab(v as 'PENDING' | 'COLLECTED')}>
        <TabsList>
          <TabsTrigger value="PENDING" className="gap-2">
            <Wallet className="h-4 w-4" />
            Pending
            {!pendingLoading && pendingConsolidated.length > 0 ? (
              <Badge variant="secondary" className="ml-1">
                {pendingConsolidated.length}
              </Badge>
            ) : null}
          </TabsTrigger>
          <TabsTrigger value="COLLECTED" className="gap-2">
            <CheckCircle2 className="h-4 w-4" />
            Collected
            {!collectedLoading && collectedConsolidated.length > 0 ? (
              <Badge variant="secondary" className="ml-1">
                {collectedConsolidated.length}
              </Badge>
            ) : null}
          </TabsTrigger>
        </TabsList>

        <TabsContent value={tab} className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                {isPendingTab ? (
                  <>
                    <Wallet className="h-5 w-5" />
                    Awaiting collection
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                    Collected
                  </>
                )}
                <Badge variant="secondary">{consolidatedRows.length}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <p className="text-sm text-muted-foreground">Loading…</p>
              ) : consolidatedRows.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  {isPendingTab
                    ? 'No pending third-party collections. These appear when Advance, Top Up, or Full & final is recorded with “amount pending from third party”.'
                    : 'No collected records yet.'}
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Patient</TableHead>
                        <TableHead>IPD #</TableHead>
                        <TableHead>Consolidated pending</TableHead>
                        {!isPendingTab && <TableHead>Settled</TableHead>}
                        {!isPendingTab && <TableHead>% diff</TableHead>}
                        <TableHead>Financing partner</TableHead>
                        <TableHead>Markers</TableHead>
                        {isPendingTab ? (
                          <TableHead className="text-right">Ageing</TableHead>
                        ) : null}
                        <TableHead>{isPendingTab ? 'Latest recorded' : 'Collected'}</TableHead>
                        {!isPendingTab && <TableHead>Collected by</TableHead>}
                        <TableHead>Notes</TableHead>
                        <TableHead className="text-right">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {consolidatedRows.map((row) => (
                        <TableRow key={row.admissionId}>
                          <TableCell>
                            <div className="font-medium">{row.patientName ?? '—'}</div>
                            <div className="text-xs text-muted-foreground">
                              {row.patientUhid ?? '—'}
                              {row.patientPhone ? ` · ${row.patientPhone}` : ''}
                            </div>
                          </TableCell>
                          <TableCell>{row.ipdNumber ?? '—'}</TableCell>
                          <TableCell className="font-semibold text-amber-800">
                            {formatCurrency(row.billedTotal)}
                          </TableCell>
                          {!isPendingTab && (
                            <TableCell className="font-semibold text-emerald-700">
                              {formatCurrency(row.settledTotal)}
                            </TableCell>
                          )}
                          {!isPendingTab && (
                            <TableCell className="text-sm text-muted-foreground">
                              {settlementDiffPercent(row.billedTotal, row.settledTotal)}
                            </TableCell>
                          )}
                          <TableCell>
                            {row.partnerLabel ? (
                              <Badge variant="outline" className="font-normal">
                                {row.partnerLabel}
                              </Badge>
                            ) : (
                              '—'
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge variant={isPendingTab ? 'secondary' : 'default'} className={!isPendingTab ? 'bg-emerald-600' : undefined}>
                              {row.count} {row.count === 1 ? 'row' : 'rows'}
                            </Badge>
                          </TableCell>
                          {isPendingTab ? (
                            <TableCell className="text-right text-sm tabular-nums whitespace-nowrap">
                              {(() => {
                                const days = ipdPendingAgeingDays(row.dischargeDate);
                                if (days == null) {
                                  return (
                                    <span className="text-muted-foreground" title="Discharge date not recorded">
                                      —
                                    </span>
                                  );
                                }
                                const tone =
                                  days >= 30
                                    ? 'font-semibold text-red-700'
                                    : days >= 14
                                      ? 'font-medium text-amber-800'
                                      : 'text-muted-foreground';
                                return (
                                  <span className={tone} title="Days since discharge (IST calendar)">
                                    {formatAgeingLabel(days)}
                                  </span>
                                );
                              })()}
                            </TableCell>
                          ) : null}
                          <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                            {row.latestAt
                              ? format(new Date(row.latestAt), 'dd MMM yyyy, HH:mm')
                              : '—'}
                          </TableCell>
                          {!isPendingTab && (
                            <TableCell className="text-sm">{row.collectedByName ?? '—'}</TableCell>
                          )}
                          <TableCell className="max-w-[180px] truncate text-sm">
                            {row.notes ?? '—'}
                          </TableCell>
                          <TableCell className="text-right space-x-2 whitespace-nowrap">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() =>
                                navigate(`${basePath}/ipd/${row.admissionId}/billing`)
                              }
                            >
                              Billing
                            </Button>
                            {isPendingTab && canDeletePendingThirdParty && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-destructive border-destructive/40 hover:bg-destructive/10"
                                disabled={deletePendingMut.isPending}
                                onClick={() => {
                                  const label =
                                    row.count > 1
                                      ? `${row.count} pending amounts totaling ${formatCurrency(row.billedTotal)}`
                                      : `pending amount ${formatCurrency(row.billedTotal)}`;
                                  if (
                                    !window.confirm(
                                      `Delete ${label} for this IPD completely? Linked cheque marker receipt(s) will also be removed.`
                                    )
                                  ) {
                                    return;
                                  }
                                  deletePendingMut.mutate(row.rows);
                                }}
                              >
                                <Trash2 className="h-4 w-4 mr-1" />
                                Delete
                              </Button>
                            )}
                            {isPendingTab && canMutateIpd && (
                              <Button
                                size="sm"
                                onClick={() => {
                                  setCollectTarget(row);
                                  setCollectForm({
                                    paymentMode: 'BANK_TRANSFER',
                                    settledAmount: String(row.billedTotal),
                                    receiptNumber: '',
                                    paymentDate: toIstDatetimeLocalValue(new Date()),
                                    notes: '',
                                  });
                                }}
                              >
                                Mark collected
                              </Button>
                            )}
                            {!isPendingTab && canDeletePendingThirdParty && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setReviseTarget(row);
                                  setReviseForm({
                                    settledAmount: String(row.settledTotal),
                                    notes: '',
                                  });
                                }}
                              >
                                <Pencil className="h-4 w-4 mr-1" />
                                Revise settled
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                    <TableFooter>
                      <TableRow className="bg-muted/40 font-semibold">
                        <TableCell colSpan={2}>
                          Total ({consolidatedRows.length}{' '}
                          {consolidatedRows.length === 1 ? 'IPD' : 'IPDs'})
                        </TableCell>
                        <TableCell className="text-amber-800">
                          {formatCurrency(tabBilledTotal)}
                        </TableCell>
                        {!isPendingTab && (
                          <TableCell className="text-emerald-700">
                            {formatCurrency(tabSettledTotal)}
                          </TableCell>
                        )}
                        {!isPendingTab && (
                          <TableCell className="text-muted-foreground">
                            {settlementDiffPercent(tabBilledTotal, tabSettledTotal)}
                          </TableCell>
                        )}
                        <TableCell colSpan={isPendingTab ? 5 : 6} />
                      </TableRow>
                    </TableFooter>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={!!reviseTarget} onOpenChange={(o) => !o && setReviseTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Revise collected settlement amount</DialogTitle>
          </DialogHeader>
          {reviseTarget && (
            <div className="space-y-4">
              <div className="rounded-md border bg-muted/40 p-3 text-sm space-y-1">
                <p>
                  <span className="text-muted-foreground">Patient:</span>{' '}
                  <strong>{reviseTarget.patientName}</strong>
                </p>
                <p>
                  <span className="text-muted-foreground">IPD #:</span>{' '}
                  <strong>{reviseTarget.ipdNumber ?? '—'}</strong>
                </p>
                <p>
                  <span className="text-muted-foreground">Consolidated billed:</span>{' '}
                  <strong className="text-amber-800">
                    {formatCurrency(reviseTarget.billedTotal)}
                  </strong>
                </p>
                <p>
                  <span className="text-muted-foreground">Current settled (revenue):</span>{' '}
                  <strong className="text-emerald-700">
                    {formatCurrency(reviseTarget.settledTotal)}
                  </strong>
                </p>
              </div>
              <div>
                <Label>Revised settlement amount (₹) — bank deposit + TDS</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  max={reviseTarget.billedTotal}
                  value={reviseForm.settledAmount}
                  onChange={(e) =>
                    setReviseForm((f) => ({ ...f, settledAmount: e.target.value }))
                  }
                />
                <p className="text-xs text-muted-foreground mt-1">
                  This amount is used as IPD revenue in P&amp;L and cash-flow reports. Usually ≤{' '}
                  {formatCurrency(reviseTarget.billedTotal)}.
                </p>
              </div>
              <div>
                <Label>Reason for revision (optional)</Label>
                <Textarea
                  value={reviseForm.notes}
                  onChange={(e) => setReviseForm((f) => ({ ...f, notes: e.target.value }))}
                  placeholder="e.g. Correcting CareFi bank deposit amount"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setReviseTarget(null)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                const settled = parseFloat(reviseForm.settledAmount);
                if (!Number.isFinite(settled) || settled <= 0) {
                  toast.error('Enter a valid settled amount');
                  return;
                }
                if (reviseTarget && settled > reviseTarget.billedTotal + 0.009) {
                  toast.error('Settled amount cannot exceed consolidated billed amount');
                  return;
                }
                if (
                  reviseTarget &&
                  Math.abs(settled - reviseTarget.settledTotal) <= 0.009
                ) {
                  toast.error('Enter a different amount from the current settled figure');
                  return;
                }
                reviseSettledMut.mutate();
              }}
              disabled={reviseSettledMut.isPending}
            >
              {reviseSettledMut.isPending ? 'Saving…' : 'Save revision'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!collectTarget} onOpenChange={(o) => !o && setCollectTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mark consolidated amount as collected</DialogTitle>
          </DialogHeader>
          {collectTarget && (
            <div className="space-y-4">
              <div className="rounded-md border bg-muted/40 p-3 text-sm space-y-1">
                <p>
                  <span className="text-muted-foreground">Patient:</span>{' '}
                  <strong>{collectTarget.patientName}</strong>
                </p>
                <p>
                  <span className="text-muted-foreground">IPD #:</span>{' '}
                  <strong>{collectTarget.ipdNumber ?? '—'}</strong>
                </p>
                <p>
                  <span className="text-muted-foreground">Consolidated pending:</span>{' '}
                  <strong className="text-amber-800">
                    {formatCurrency(collectTarget.billedTotal)}
                  </strong>
                  {collectTarget.count > 1 ? (
                    <span className="text-muted-foreground">
                      {' '}
                      ({collectTarget.count} open markers)
                    </span>
                  ) : null}
                </p>
                {collectTarget.partnerLabel ? (
                  <p>
                    <span className="text-muted-foreground">Financing partner:</span>{' '}
                    <strong>{collectTarget.partnerLabel}</strong>
                  </p>
                ) : null}
              </div>
              <div>
                <Label>Final settlement amount (₹) — bank deposit + TDS</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  max={collectTarget.billedTotal}
                  value={collectForm.settledAmount}
                  onChange={(e) =>
                    setCollectForm((f) => ({ ...f, settledAmount: e.target.value }))
                  }
                />
                <p className="text-xs text-muted-foreground mt-1">
                  One settlement closes all open third-party pendings for this IPD. Usually ≤{' '}
                  {formatCurrency(collectTarget.billedTotal)}. This amount becomes IPD revenue.
                </p>
              </div>
              <div>
                <Label>Payment mode</Label>
                <Select
                  value={collectForm.paymentMode}
                  onValueChange={(v: PaymentMethod) =>
                    setCollectForm((f) => ({ ...f, paymentMode: v }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAYMENT_METHODS.map((m) => (
                      <SelectItem key={m} value={m}>
                        {m}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Payment date &amp; time (IST)</Label>
                <Input
                  type="datetime-local"
                  value={collectForm.paymentDate}
                  onChange={(e) =>
                    setCollectForm((f) => ({ ...f, paymentDate: e.target.value }))
                  }
                />
              </div>
              <div>
                <Label>Receipt number (optional)</Label>
                <Input
                  value={collectForm.receiptNumber}
                  onChange={(e) =>
                    setCollectForm((f) => ({ ...f, receiptNumber: e.target.value }))
                  }
                />
              </div>
              <div>
                <Label>Notes (optional)</Label>
                <Textarea
                  value={collectForm.notes}
                  onChange={(e) => setCollectForm((f) => ({ ...f, notes: e.target.value }))}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCollectTarget(null)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                const settled = parseFloat(collectForm.settledAmount);
                if (!Number.isFinite(settled) || settled <= 0) {
                  toast.error('Enter a valid settled amount');
                  return;
                }
                if (collectTarget && settled > collectTarget.billedTotal + 0.009) {
                  toast.error('Settled amount cannot exceed consolidated pending amount');
                  return;
                }
                collectMut.mutate();
              }}
              disabled={collectMut.isPending}
            >
              {collectMut.isPending ? 'Saving…' : 'Confirm collected'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
